import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, Mail, Loader2, ShieldCheck, ArrowLeft } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { KRAFT } from '@/constants/testIds';

export default function Login() {
  const navigate = useNavigate();
  const { status, login } = useAuth();
  const [form, setForm] = useState({ email: '', password: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (status === 'authed') navigate('/admin', { replace: true });
  }, [status, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !form.password || submitting) return;
    setError('');
    setSubmitting(true);
    const result = await login(form.email.trim(), form.password);
    setSubmitting(false);
    if (result.ok) navigate('/admin', { replace: true });
    else setError(result.error || 'Nie udało się zalogować');
  };

  return (
    <div
      data-testid={KRAFT.adminLoginRoot}
      className="min-h-screen bg-ink-950 flex flex-col relative overflow-hidden"
    >
      {/* subtle grid overlay */}
      <div className="absolute inset-0 grid-lines opacity-30 pointer-events-none" />
      <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-volt/5 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full bg-volt/5 blur-3xl pointer-events-none" />

      {/* Header */}
      <header className="relative z-10 border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-3 text-slate-300 hover:text-white transition-colors group"
          >
            <span className="w-9 h-9 border border-white/10 flex items-center justify-center group-hover:border-volt transition-colors">
              <ArrowLeft className="w-4 h-4" />
            </span>
            <span className="font-mono text-[11px] uppercase tracking-[0.22em]">Strona główna</span>
          </Link>
          <div className="leading-tight text-right">
            <div className="font-display uppercase text-white text-base tracking-tight">Kraft·Performance</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-slate-400">Panel wewnętrzny</div>
          </div>
        </div>
      </header>

      {/* Card */}
      <main className="relative z-10 flex-1 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-md bg-ink-900 border border-white/10 relative"
        >
          {/* Top accent */}
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-volt to-transparent" />

          <div className="px-8 pt-10 pb-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 border border-volt/60 flex items-center justify-center">
                <ShieldCheck className="w-4 h-4 text-volt" strokeWidth={2} />
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-volt">Autoryzacja</div>
                <div className="font-display uppercase text-white text-xl tracking-tight leading-tight">Logowanie</div>
              </div>
            </div>

            <p className="text-slate-400 text-sm leading-relaxed mb-8">
              Dostęp tylko dla personelu Kraft-Performance. Dane rezerwacji i wiadomości klientów chronione
              zgodnie z zasadą minimalizacji.
            </p>

            <form onSubmit={submit} className="space-y-5">
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">Adres e-mail</label>
                <div className="relative mt-2">
                  <Mail className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    data-testid={KRAFT.adminLoginEmail}
                    type="email"
                    autoComplete="username"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    required
                    className="w-full h-12 pl-10 pr-4 bg-ink-950 border border-white/10 text-white text-sm font-mono placeholder:text-slate-600 focus:outline-none focus:border-volt transition-colors"
                    placeholder="np. admin@kraft-performance.pl"
                  />
                </div>
              </div>

              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">Hasło</label>
                <div className="relative mt-2">
                  <Lock className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    data-testid={KRAFT.adminLoginPassword}
                    type="password"
                    autoComplete="current-password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    required
                    className="w-full h-12 pl-10 pr-4 bg-ink-950 border border-white/10 text-white text-sm font-mono placeholder:text-slate-600 focus:outline-none focus:border-volt transition-colors"
                    placeholder="••••••••••••"
                  />
                </div>
              </div>

              {error && (
                <motion.div
                  data-testid={KRAFT.adminLoginError}
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="border border-red-500/40 bg-red-500/10 p-3 text-red-300 text-sm font-mono"
                >
                  {error}
                </motion.div>
              )}

              <button
                data-testid={KRAFT.adminLoginSubmit}
                type="submit"
                disabled={submitting || !form.email || !form.password}
                className="btn-volt w-full justify-center disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Weryfikacja...
                  </>
                ) : (
                  <>
                    <Lock className="w-4 h-4" />
                    Zaloguj się do panelu
                  </>
                )}
              </button>
            </form>

            <div className="mt-8 pt-6 border-t border-white/10 flex items-center gap-2 text-slate-500">
              <div className="w-1.5 h-1.5 bg-volt" />
              <span className="font-mono text-[10px] uppercase tracking-[0.22em]">
                Sesja JWT · ważność 7 dni · szyfrowanie bcrypt
              </span>
            </div>
          </div>
        </motion.div>
      </main>

      <footer className="relative z-10 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-4 flex items-center justify-between text-slate-500 font-mono text-[10px] uppercase tracking-[0.22em]">
          <span>Kraft-Performance · Panel wewnętrzny</span>
          <span>Chroniony obszar · brak śledzenia</span>
        </div>
      </footer>
    </div>
  );
}
